import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import ax from "../../config/axios";
import Plot from "react-plotly.js";
import styles from "./COTAnalysis.module.css";
import { RollData, SpreadVolumes } from "./COTAnalysisSlice";
import "bootstrap-icons/font/bootstrap-icons.css";

// Stable references so Plotly isn't handed brand-new prop objects each render.
const plotConfig = { responsive: true } as const;

// Stable palette for OI traces, indexed by the symbol's fixed position in
// the master list so colors don't shift as symbols are toggled.
const oiColors = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
];


export function RootView({
  root,
  onClose,
}: {
  root: string;
  onClose: () => void;
}) {
  //Feature Flags
  const ratingLimitFlag = true;
  ///////////////////////////////////////
  const [oiData, setOiData] = useState<{ symbol: string, data: { tradingday: string, DTFN: number, openinterest: number }[] }[][] | null>(null);
  const [viewableOiData, setViewableOiData] = useState<string[]>([]);

  // Flattened master list + a stable symbol->color map derived from the API
  // data, so trace order and colors stay fixed as symbols are toggled.
  const oiFlat = useMemo(() => (oiData ?? []).flat(), [oiData]);
  const oiColorBySymbol = useMemo(
    () =>
      Object.fromEntries(
        oiFlat.map((oi, idx) => [oi.symbol, oiColors[idx % oiColors.length]]),
      ) as Record<string, string>,
    [oiFlat],
  );
  const [isOITruncated, setIsOITruncated] = useState<boolean>(true);
  const [cotChartData, setCotChartData] = useState(null);
  const [rollData, setRollData] = useState<RollData>();
  const [spreadVolumes, setSpreadVolumes] = useState<SpreadVolumes[]>([]);
  const [viewableSpreads, setViewableSpreads] = useState<string[]>([]);
  const [rawView, setRawView] = useState<boolean>(false);
  const [rollStats, setRollStats] = useState<{
    active_contract: string;
    crossings_end: number;
    crossings_start: number;
    first_notice_date: string;
    rating: number;
    roll_end: number;
    roll_end_date: string;
    roll_start: number;
    roll_start_date: string;
    root: string;
    current_contract_idx: number;
    non_current_rolls: {
      active_contract: string;
      first_notice_date: string;
      roll_end_date: string;
      roll_start_date: string;
      root: string;
    }[];
  } | null>(null);
  const [mmOIStrength, setMmOIStrength] = useState<
    {
      cot_date: string;
      mm_pct_oi: number;
    }[]
  >([]);

  const [flatPrice, setFlatPrice] = useState<Array<{
    tradingDay: string;
    open: number;
    high: number;
    low: number;
    close: number;
    openInterest: number;
  }> | null>(null);

  const [futuresGaps, setFuturesGaps] = useState<{ bounds: string[] }[]>([]);

  const [cotHistory, setCotHistory] = useState<
    Array<{
      Root: string;
      Date: string;
      MM_Net: number;
      MM_Net_Change: number | null;
    }>
  >([]);

  const [mm_oi_pcts, setMm_oi_pcts] = useState<
    { Date: string; mm_oi_pct: number }[] | null
  >(null);

  const [histRollIndex, setHistRollIndex] = useState(0);

  // useEffect(() => {
  //   console.log("gaps calc");
  //   const combinedDates = Array.from(
  //     new Set(
  //       [
  //         ...(flatPrice || []),
  //         ...(cotHistory || []),
  //         ...[{ Date: "2019-12-24" }],
  //       ].map((d: any) => new Date(d.tradingDay || d.Date).toISOString()),
  //     ),
  //   )
  //     .map((dateStr) => new Date(dateStr))
  //     .sort((a, b) => a.getTime() - b.getTime());

  //   console.log(
  //     combinedDates.some(
  //       (d) => d.getTime() === new Date("2019-01-01").getTime(),
  //     ),
  //   );

  //   const gaps: { bounds: string[] }[] = [];
  //   if (combinedDates.length > 0) {
  //     for (let i = 1; i < combinedDates.length; i++) {
  //       const diff =
  //         (combinedDates[i].getTime() - combinedDates[i - 1].getTime()) /
  //         (1000 * 60 * 60 * 24); // days
  //       if (diff > 1) {
  //         gaps.push({
  //           bounds: [
  //             combinedDates[i - 1].toISOString().split("T")[0],
  //             combinedDates[i].toISOString().split("T")[0],
  //           ],
  //         });
  //       }
  //     }
  //   }

  //   setFuturesGaps(gaps);
  //   console.log(combinedDates);
  //   console.log(gaps);
  // }, [flatPrice, cotHistory]);

  // Scroll to top of the page on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    });
  }, [root]);

  useEffect(() => {
    if (flatPrice && cotHistory.length > 0) {
      const mm_oi_ratio = [] as Array<{ Date: string; mm_oi_pct: number }>;
      for (const cotObj of cotHistory) {
        const dateStr = cotObj.Date;
        flatPrice
          .filter((item) => item.tradingDay === dateStr)
          .map(
            (item) =>
              item.openInterest !== 0 &&
              mm_oi_ratio.push({
                Date: dateStr,
                mm_oi_pct: Math.abs(cotObj.MM_Net / item.openInterest),
              }),
          );
      }
      setMm_oi_pcts(mm_oi_ratio);
    }
  }, [cotHistory, flatPrice]);

  useEffect(() => {
    // const formattedStartDate = new Date(
    //   Date.now() - 4 * 365 * 24 * 60 * 60 * 1000,
    // )
    //   .toISOString()
    //   .slice(0, 10)
    //   .replace(/-/g, "");
    if (cotHistory.length > 0) {
      const oldestDate = cotHistory
        .map((entry) => new Date(entry.Date))
        .sort((a, b) => a.getTime() - b.getTime())[0]
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "");

      const controller = new AbortController();
      ax.get("data/futures-history", {
        params: { root: root, startDate: oldestDate },
        signal: controller.signal,
      })
        .then((response) => {
          setFlatPrice(response.data.results);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) throw error;
        });
      return () => controller.abort();
    }
  }, [root, cotHistory]);

  useEffect(() => {
    const controller = new AbortController();
    ax.get("data/root-details", {
      params: { root: root },
      signal: controller.signal,
    })
      .then((response) => {
        setRollData(response.data);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error;
      });
    return () => controller.abort();
  }, [root]);

  useEffect(() => {
    const controller = new AbortController();
    ax.get("data/spread-volumes", {
      params: { root: root },
      signal: controller.signal,
    })
      .then((response) => {
        setSpreadVolumes(response.data);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error;
      });
    return () => controller.abort();
  }, [root]);

  useEffect(() => {
    const controller = new AbortController();
    ax.get("data/roll-stats", {
      params: { root: root },
      signal: controller.signal,
    })
      .then((response) => {
        setRollStats(response.data);
        setHistRollIndex(response.data.current_contract_idx);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error;
      });
    return () => controller.abort();
  }, [root]);

  useEffect(() => {
    const controller = new AbortController();
    ax.get("data/cot-history", {
      params: { root: root },
      signal: controller.signal,
    })
      .then((response) => {
        setCotHistory(response.data);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error;
      });
    return () => controller.abort();
  }, [root]);

  useEffect(() => {
    const controller = new AbortController();
    ax.get("data/get-roll-strength", {
      params: { root: root },
      signal: controller.signal,
    })
      .then((response) => {
        setMmOIStrength(response.data[root]);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error;
      });
    return () => controller.abort();
  }, [root]);

  useEffect(() => {
    const controller = new AbortController();
    ax.get("data/historical-open-interest", {
      params: { root: root },
      signal: controller.signal,
    })
      .then((response) => {
        setOiData(response.data);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error;
      });
    return () => controller.abort();
  }, [root]);

  const currentDatePlotX = (rollStats?.roll_end ?? 0) +
    (new Date(rollStats?.roll_end_date || "").getTime() -
      new Date().getTime()) /
    (1000 * 60 * 60 * 24);

  // These charts depend only on fetched data, not on the checkbox/roll
  // controls, so memoizing their elements keeps them from being re-rendered
  // (and redrawn by Plotly) every time an unrelated control is toggled.
  const flatPriceChart = useMemo(
    () =>
      !flatPrice ? (
        <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
      ) : (
        <Plot
          className="m-1 h-100"
          data={[
            {
              type: "candlestick",
              x: flatPrice.map((point) => point.tradingDay),
              open: flatPrice.map((point) => point.open),
              high: flatPrice.map((point) => point.high),
              low: flatPrice.map((point) => point.low),
              close: flatPrice.map((point) => point.close),
              yaxis: "y1",
              name: root + "*1",
            },
            {
              type: "scatter",
              mode: "lines",
              line: { shape: "hv" },
              name: "MM_Net",
              x: cotHistory.map((point) => point.Date),
              y: cotHistory.map((point) => point.MM_Net),
              yaxis: "y2",
            },
          ]}
          layout={{
            title: "Flat Price of " + root + "*1",
            xaxis: { type: "date" },
            yaxis: { title: "Price", side: "left" },
            yaxis2: { title: "MM_Net", overlaying: "y", side: "right" },
          }}
          config={plotConfig}
        />
      ),
    [flatPrice, cotHistory, root],
  );

  const mmNetHistogram = useMemo(
    () =>
      cotHistory.length === 0 ? (
        <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
      ) : (
        <Plot
          className="h-100 w-100"
          data={[
            {
              x: cotHistory.map((point) => point.MM_Net),
              type: "histogram",
            },
          ]}
          layout={{
            title: "MM Net Position Histogram",
            margin: { l: 50, r: 50, t: 50, b: 30 },
            shapes: [
              {
                type: "line" as const,
                x0: cotHistory[cotHistory.length - 1].MM_Net,
                x1: cotHistory[cotHistory.length - 1].MM_Net,
                yref: "paper" as const,
                y0: 0.0,
                y1: 1,
                line: { color: "red", width: 2, dash: "dashdot" as const },
              },
            ],
          }}
          config={plotConfig}
        />
      ),
    [cotHistory],
  );

  const mmOIStrengthHistogram = useMemo(
    () =>
      !mmOIStrength ||
      mmOIStrength.length === 0 ||
      !mm_oi_pcts ||
      mm_oi_pcts.length === 0 ? (
        <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
      ) : (
        <Plot
          className="h-100 w-100"
          data={[
            {
              x: mmOIStrength.map((point) => point.mm_pct_oi),
              type: "histogram",
            },
          ]}
          layout={{
            title: "MM Net OI Roll Strength Histogram",
            margin: { l: 50, r: 50, t: 50, b: 30 },
            shapes: [
              {
                type: "line" as const,
                x0: mm_oi_pcts?.[mm_oi_pcts.length - 1].mm_oi_pct,
                x1: mm_oi_pcts?.[mm_oi_pcts.length - 1].mm_oi_pct,
                yref: "paper" as const,
                y0: 0.0,
                y1: 1,
                line: { color: "red", width: 2, dash: "dashdot" as const },
              },
            ],
          }}
          config={plotConfig}
        />
      ),
    [mmOIStrength, mm_oi_pcts],
  );

  const mmNetChangeHistogram = useMemo(
    () =>
      cotHistory.length === 0 ? (
        <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
      ) : (
        <Plot
          className="h-100 w-100"
          data={[
            {
              x: cotHistory.map((point) => point.MM_Net_Change),
              type: "histogram",
            },
          ]}
          layout={{
            title: "MM Net Change Histogram",
            margin: { l: 50, r: 50, t: 50, b: 30 },
            shapes: [
              {
                type: "line" as const,
                x0: cotHistory[cotHistory.length - 1].MM_Net_Change,
                x1: cotHistory[cotHistory.length - 1].MM_Net_Change,
                yref: "paper" as const,
                y0: 0.0,
                y1: 1,
                line: { color: "red", width: 2, dash: "dashdot" as const },
              },
            ],
          }}
          config={plotConfig}
        />
      ),
    [cotHistory],
  );

  const oiCotHistoryChart = useMemo(
    () =>
      !(flatPrice && cotHistory.length > 0) ? (
        <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
      ) : (
        <Plot
          className="w-100 h-100"
          data={[
            {
              type: "scatter",
              mode: "lines",
              name: "Open Interest",
              x: flatPrice.map((point) => point.tradingDay),
              y: flatPrice.map((point) => point.openInterest),
              yaxis: "y1",
            },
            {
              type: "scatter",
              mode: "lines",
              line: { shape: "hv" },
              name: "MM Pct OI",
              x: mm_oi_pcts?.map((point) => point.Date),
              y: mm_oi_pcts?.map((point) => point.mm_oi_pct),
              yaxis: "y3",
            },
            {
              type: "scatter",
              mode: "lines",
              line: { shape: "hv" },
              name: "MM_Net",
              x: cotHistory.map((point) => point.Date),
              y: cotHistory.map((point) => point.MM_Net),
              yaxis: "y2",
            },
          ]}
          layout={{
            margin: { b: 25 },
            title:
              "Open Interest, MM Net Position, & PCT of OI held by MM ",
            xaxis: { type: "date", domain: [0.0, 0.9] },
            yaxis: { title: "OI", side: "left" },
            yaxis2: { title: "MM_Net", overlaying: "y", side: "right" },
            yaxis3: {
              tickformat: ".2%",
              title: "Pct",
              overlaying: "y",
              side: "right",
              anchor: "free",
              position: 0.955,
            },
          }}
          config={plotConfig}
        />
      ),
    [flatPrice, cotHistory, mm_oi_pcts],
  );

  return (
    <div className="w-100 h-100 p-3">
      <div className="card w-100 h-100 p-0">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h2 className="mb-0 fw-semibold">Root Details: {root}</h2>
          <button
            className="btn btn-close"
            onClick={onClose}
            style={{ transform: "scale(1.5)", transformOrigin: "center" }}
          />
        </div>
        <div className="card-body h-100" style={{ backgroundColor: "#f9fafb" }}>
          {/* Spread Volume */}
          <div
            className="d-flex flex-row gap-1 pb-3"
            style={{ height: 375, minHeight: 350 }}
          >
            <div
              className="card flex-shrink-0"
              style={{ height: "100%", maxHeight: "50vh" }}
            >
              <div className="card-header">
                <h2 className="mb-0">Spread Volumes</h2>
              </div>

              {spreadVolumes.length === 0 ? (
                <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
              ) : (
                <div className="card-body overflow-y-scroll">
                  <div className="form-check px-0">
                    {spreadVolumes.map((spread, idx) => (
                      <div key={idx} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`spreadCheckbox${idx}`}
                          checked={viewableSpreads.includes(spread.symbol)}
                          onChange={() => {
                            if (viewableSpreads.includes(spread.symbol)) {
                              setViewableSpreads(
                                viewableSpreads.filter(
                                  (c) => c !== spread.symbol,
                                ),
                              );
                            } else {
                              setViewableSpreads([
                                spread.symbol,
                                ...viewableSpreads,
                              ]);
                            }
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`spreadCheckbox${idx}`}
                        >
                          {spread.symbol} ({spread.entries})
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="card-footer mt-auto">
                {/* create a slider switch to set raw view to on/off */}
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="flexSwitchCheckDefault"
                    onChange={() => setRawView(!rawView)}
                    checked={rawView && spreadVolumes.length > 0}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="flexSwitchCheckDefault"
                  >
                    Raw View
                  </label>
                </div>
              </div>
            </div>
            <div className="card flex-grow-1 p-1 h-100 position-relative">
              {rollData === null || rollData === undefined ? (
                <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
              ) : (
                <>
                  <Plot
                    className="h-100"
                    data={[
                      {
                        type: "scatter",
                        mode: "lines",
                        name: "avg_volume",
                        x: rollData.data.avg_volume
                          .filter(
                            (point) =>
                              (point.spread_DTFN >= -10 &&
                                point.spread_DTFN <= 100) ||
                              rawView,
                          )
                          .map((point) => point.spread_DTFN),
                        y: rollData.data.avg_volume
                          .filter(
                            (point) =>
                              (point.spread_DTFN >= -10 &&
                                point.spread_DTFN <= 100) ||
                              rawView,
                          )
                          .map((point) => point.volume),
                      } as Partial<Plotly.ScatterData>,
                      ...spreadVolumes
                        ?.filter((spread) =>
                          viewableSpreads.includes(spread.symbol),
                        )
                        .map((spread, index) => {
                          const filteredData = spread.data.filter(
                            (point) =>
                              (point.spread_DTFN >= -10 &&
                                point.spread_DTFN <= 100) ||
                              rawView,
                          );
                          return {
                            type: "scatter",
                            mode: "lines",
                            x: filteredData.map((point) => point.spread_DTFN),
                            y: filteredData.map((point) => point.volume),
                            text: filteredData.map((point) =>
                              new Date(point.tradingDay).toLocaleDateString(), 
                            ),
                            name: spread.symbol,
                            hoverinfo: "x+y+text+name" as const, // @ts-ignore //edited plotly types to fix this
                          } as Partial<Plotly.ScatterData>; // @ts-ignore
                        }),
                      ...oiFlat
                        .filter((oi) => viewableOiData.includes(oi.symbol))
                        .flatMap((oi) => {
                          const oiData = oi.data
                            .filter(
                              (point) =>
                                ((point.DTFN >= -10 && point.DTFN <= 100) ||
                                  !isOITruncated) &&
                                point.openinterest !== 0,
                          )
                            .sort((a, b) => a.DTFN - b.DTFN);
                          return [
                            {
                              type: "scatter",
                              mode: "lines",
                              name: oi.symbol + " OI",
                              x: oiData.map((point) => point.DTFN),
                              y: oiData.map((point) => point.openinterest),
                              text: oiData.map((point) =>
                                new Date(point.tradingday).toLocaleDateString(),
                              ),
                              hoverinfo: "x+y+text+name" as const,
                              yaxis: "y2",
                              line: {
                                dash: "dot" as const,
                                color: oiColorBySymbol[oi.symbol],
                              },
                            } as Partial<Plotly.ScatterData>,
                          ];
                        }),
                    ]}
                    layout={{
                      title: "Calendar Spread Volumes for " + root,
                      xaxis: {
                        title: "Calendar Days to First Notice (DTFN)",
                        autorange: "reversed", // Reverse the x-axis
                      },
                      yaxis: {
                        title: "Volume",
                      },
                      yaxis2: {
                        title: "Open Interest",
                        overlaying: "y",
                        side: "right",
                      },
                      legend: { x: 1.05 },

                      shapes:
                        (rollStats?.rating ?? 0) > 2 || ratingLimitFlag
                          ? [
                              {
                                type: "line" as const,
                                x0: rollStats?.roll_start || 0,
                                x1: rollStats?.roll_start || 0,
                                yref: "paper" as const,
                                y0: 0.05,
                                y1: 1.05,
                                line: {
                                  color: "red",
                                  width: 2,
                                  dash: "dashdot" as const,
                                },
                              },
                              {
                                type: "line" as const,
                                x0: rollStats?.roll_end || 0,
                                x1: rollStats?.roll_end || 0,
                                yref: "paper" as const,
                                y0: 0.05,
                                y1: 1.05,
                                line: {
                                  color: "blue",
                                  width: 2,
                                  dash: "dashdot" as const,
                                },
                              },

                              //Current date position in roll
                              ...(histRollIndex ===
                              rollStats?.current_contract_idx
                                ? [
                                    {
                                      type: "line" as const,
                                      x0: currentDatePlotX,
                                      x1: currentDatePlotX,
                                      yref: "paper" as const,
                                      y0: 0.05,
                                      y1: 1.05,
                                      line: {
                                        color: "green",
                                        width: 2,
                                        dash: "dashdot" as const,
                                      },
                                    },
                                  ]
                                : []),
                            ]
                          : [],
                      annotations:
                        (rollStats?.rating ?? 0) > 2 || ratingLimitFlag
                          ? [
                              {
                                xref: "x" as const,
                                yref: "paper" as const,
                                x: rollStats?.roll_start || 0,
                                y: -0.2,
                                text: `<b>Roll Start: ${rollStats?.non_current_rolls[histRollIndex]?.roll_start_date}</b>`,

                                showarrow: false,
                              },
                              {
                                xref: "x" as const,
                                yref: "paper" as const,
                                x: rollStats?.roll_end || 0,
                                y:
                                  -0.2 -
                                  (rollStats?.roll_start === rollStats?.roll_end
                                    ? 0.1
                                    : 0.075), //offset if overlapping
                                text: `<b>Roll End: ${rollStats?.non_current_rolls[histRollIndex]?.roll_end_date}</b>`,
                                showarrow: false,
                              },
                              //current date position
                              ...(histRollIndex ===
                              rollStats?.current_contract_idx
                                ? [
                                    {
                                      xref: "x" as const,
                                      yref: "paper" as const,
                                      x: currentDatePlotX,
                                      y: 1.18,
                                      text: "Today",
                                      showarrow: false,
                                    },
                                  ]
                                : []),
                            ]
                          : [],
                    }}
                    config={plotConfig}
                  />
                  <div
                    className="z-3 position-absolute top-0 d-flex flex-column"
                    style={{ top: 0, margin: 10 }}
                  >
                    <div className=" d-flex align-items-center gap-1 mb-1">
                      <button
                        className={`btn btn-primary py-0 ${
                          histRollIndex == 0 && "disabled"
                        }`}
                        onClick={() => setHistRollIndex((idx) => idx - 1)}
                      >
                        <i className="bi bi-arrow-left-short fs-4"></i>
                      </button>
                      <button
                        className="btn btn-primary  px-1 py-0 "
                        title="Return to Current Roll"
                        onClick={() =>
                          setHistRollIndex(rollStats?.current_contract_idx || 0)
                        }
                      >
                        <i className="bi bi-arrows-collapse-vertical fs-4"></i>
                      </button>
                      <button
                        className={`btn btn-primary py-0 ${
                          histRollIndex >=
                            (rollStats?.non_current_rolls?.length ?? 0) - 1 &&
                          "disabled"
                        }`}
                      >
                        <i
                          className="bi bi-arrow-right-short fs-4"
                          onClick={() => setHistRollIndex((idx) => idx + 1)}
                        ></i>
                      </button>
                      <span className="ms-2">
                        {
                          rollStats?.non_current_rolls[histRollIndex]
                            ?.active_contract
                        }
                      </span>
                    </div>
                    <div className="">
                      <span>
                        {histRollIndex + 1}/
                        {rollStats?.non_current_rolls?.length}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="card h-100 ">
              <div className="card-header">
                <h2 className="mb-0">Open Interest</h2>
                <h6 className="text-muted text-center">(Latest OI Data)</h6>
              </div>
              <div className="card-body overflow-y-scroll">
                {oiData && oiData.map((oiSection, oiSectionIdx) => {
                  return (
                    <Fragment key={oiSectionIdx}>
                      {oiSectionIdx > 0 && <hr className="my-1" />}
                      {oiSection.map((oi) => {
                        const latestOI = oi.data.find(
                          (point) => point.openinterest !== 0,
                        )?.openinterest;
                        return (
                          <div key={oi.symbol} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value={oi.symbol}
                              checked={viewableOiData.includes(oi.symbol)}
                              onChange={() => {
                                if (viewableOiData.includes(oi.symbol)) {
                                  setViewableOiData(viewableOiData.filter((c) => c !== oi.symbol));
                                } else {
                                  setViewableOiData([oi.symbol, ...viewableOiData]);
                                }
                              }}
                              id={`oi-checkbox-${oi.symbol}`}
                            // disabled={!viewableOiData.includes(oi.symbol)}
                            />
                            <label className="form-check-label" htmlFor={`oi-checkbox-${oi.symbol}`}>
                              {oi.symbol}{" "}
                              <span className="text-muted">({latestOI ? latestOI.toLocaleString() : 0})</span>
                            </label>
                          </div>
                        );
                      })}
                    </Fragment>
                  )
                })}
              </div>
              <div className="card-footer mt-auto">
                {/* create a slider switch to set raw view to on/off */}
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="OI-Check"
                    onChange={() => setIsOITruncated(!isOITruncated)}
                    checked={!isOITruncated}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="OI-Check"
                  >
                    OI Over 100 Days
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Flat price flex row */}
          <div
            className="d-flex flex-row gap-1 pb-3"
            style={{ height: 700, minHeight: 350 }}
          >
            <div
              className="d-flex flex-column w-25 h-100 gap-1"
              style={{ minWidth: 315 }}
            >
              {!rollStats ? (
                <div
                  className={`card w-100 h-50 ${styles.pulseGradient}`}
                ></div>
              ) : (
                <div className="card w-100 h-50">
                  <div className="card-header card-text d-flex justify-content-center">
                    Spread Volume Roll Stats
                  </div>
                  <div className="card-body">
                    <div>
                      <strong>Active Contract:</strong>{" "}
                      {rollStats.active_contract}
                    </div>
                    <div>
                      <strong>Confidence Rating:</strong>{" "}
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          style={{ marginLeft: 2 }}
                          className={`bi ${
                            i < (rollStats.rating ?? 0)
                              ? "bi-star-fill text-warning"
                              : "bi-star"
                          }`}
                        />
                      ))}
                    </div>
                    <div>
                      <strong>Roll Start (DTFN):</strong> {rollStats.roll_start}{" "}
                      <span className="text-muted">
                        ({rollStats.roll_start_date})
                      </span>
                    </div>
                    <div>
                      <strong>Roll End (DTFN):</strong> {rollStats.roll_end}{" "}
                      <span className="text-muted">
                        ({rollStats.roll_end_date})
                      </span>
                    </div>
                    <div>
                      <strong>Start of Volume Crossings (DTFN):</strong>{" "}
                      {rollStats.crossings_start}
                    </div>
                    <div>
                      <strong>End of Volume Crossings (DTFN):</strong>{" "}
                      {rollStats.crossings_end}
                    </div>
                  </div>
                </div>
              )}
              <div className="card w-100 h-50"></div>
              {/* <div className="card p-1 w-100 h-50">
                {cotHistory.length === 0 ? (
                  <div className={` w-100 h-100 ${styles.pulseGradient}`}></div>
                ) : (
                  <Plot
                    className="m-1 h-100"
                    data={[
                      {
                        type: "heatmap",
                        colorscale: [
                          [0, "#FF0000"], // Blue
                          [1, "#0000FF"], // Red
                        ],
                        name: "HeatMap",
                        x: ["A", "B", "C", "D", "E", "F"],
                        y: ["A", "B", "C", "D", "E", "F"],
                        z: [
                          [1, 0.29, 0.62, 0.05, 0.34, 0.49],
                          [0.29, 1, 0.48, 0.43, 0.35, 0.16],
                          [0.62, 0.48, 1, 0.62, 0.82, 0.25],
                          [0.05, 0.43, 0.62, 1, 0.15, 0.82],
                          [0.34, 0.35, 0.82, 0.15, 1, 0.9],
                          [0.49, 0.16, 0.25, 0.82, 0.9, 1],
                        ],
                      },
                    ]}
                    layout={{
                      title: "Random HeatMap",
                      xaxis: {},
                      yaxis: {},
                    }}
                    config={plotConfig}
                  />
                )}
              </div> */}
            </div>

            <div
              className="card flex-grow-1 w-75 h-100"
              // style={{ overflow: "hidden" }}
            >
              {flatPriceChart}
            </div>
          </div>
          {/* End FlatPrice */}
          {/*Histogram row */}

          <div
            className="d-flex flex-row gap-1 pb-3"
            style={{ height: 375, minHeight: 350 }}
          >
            <div className="card p-1" style={{ width: "33%" }}>
              {mmNetHistogram}
            </div>
            <div className="card p-1" style={{ width: "34%" }}>
              {mmOIStrengthHistogram}
            </div>

            <div className="card p-1" style={{ width: "33%" }}>
              {mmNetChangeHistogram}
            </div>
          </div>

          {/* OI and COT history chart row */}
          <div
            className="d-flex flex-row gap-1 pb-3"
            style={{ height: 375, minHeight: 350 }}
          >
            <div className="card w-100 pb-3">
              {oiCotHistoryChart}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RootView;
